import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  ApiButton,
  Box,
  ContentTemplate,
  HeaderTemplate,
  HtmlTrans,
  KeyValue,
  Progress,
  SectionHeader,
  TitleHeader,
  Typography,
} from 'web-library';
import { TFunction, useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';


import { useHistory, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { AxiosError, AxiosResponse } from 'axios';
import ControlledAutocomplete from 'web-library/components/form-items/ControlledAutocomplete';
import Acknowledgement from '../../../../../components/Acknowledgement';
import { candidateService } from '../../../../../api';
import {
  AdmissionExamLevelResponse,
  CreateExamRegistrationResponse,
  CreateNonExamRegistrationRequest,
  ExamRegistrationType,
  IOptionType,
  NonExamRegistrationEligibilityResponse,
} from '../../../../../api/candidate/exam/model';
import { parseLocationSearch } from '../../../../../utils/StringUtils';
import { InstructionNonNationalFormData } from '../../../../../models';
import instructionNonNationalSchema from '../../../../../modules/examRegistration/schema/instructionNonNationalSchema';
import RouteKey from '../../../../../routes/key';
import { EXAM_TYPE } from '../../../../../routes/ExamRoute';
import ConfirmNewAcademicDialog from '../../../../../modules/examRegistration/components/nonNationalExam/ConfirmNewAcademicDialog';
import NonExamNotAvailable from '../../../../../modules/examRegistration/components/NonExamNotAvailable';
import { CustomMutateReturn } from '../../../../../hooks/query';
import { GenericErrorResponse } from '../../../../../api-refactored/typings';
import { useWOGAA } from '../../../../../hooks/wogaa';

const { exam } = RouteKey.child;

const {
  examRegistrations,
  nonNationalExaminations,
  parentParticulars,
  candidateDetails,
} = RouteKey.exam.child;

const useStyles = makeStyles()((theme) =>
({
  examRegistrationPeriod: {
    marginBottom: theme.spacing(4),
  },
  getStartedContent: {
    paddingTop: theme.spacing(4),
    marginBottom: theme.spacing(7),
  },
  startApplicationButton: {
    marginTop: theme.spacing(1),
  },
  getStartedStatement: {
    marginBottom: theme.spacing(4),
  },
  continueApplicationElement: {
    marginBottom: theme.spacing(1),
  },
  link: {
    fontSize: '1.0rem',
  },
  boldContent: {
    fontWeight: 'bold',
  },
  getStartedTitle: {
    marginBottom: theme.spacing(4),
  },
  checkbox: {
    paddingLeft: 0,
  },
  levelSeekingAdmission: {
    marginBottom: theme.spacing(4),
  },
  vacancyNote: {
    fontWeight: 900,
  },
  errString: {
    color: theme.color.state.stateError,
    marginBottom: theme.spacing(2),
  },
}));

interface InstructionsContentsDetailsProps {
  isAeis: boolean;
  t: TFunction;
  classes: Record<string,string>;
}

const InstructionsContentsDetails = ({ t, classes, isAeis }: InstructionsContentsDetailsProps) => (
  <>
    {/* Read the following instructions before proceeding */}
    <Typography className={classes.boldContent} variant="body1">
        {t('exam->>Instructions.CommonStatement')}
    </Typography>
    <ol>
      <li> {/* 1 */}
        <Typography paragraph variant="body1" dangerouslySetInnerHTML={{ __html: t('exam->>Instructions.CommonItemContent') }} />
      </li>
      <br />
      <li> {/* 2 */}
        <Typography paragraph variant="body1">
          {isAeis
            ? t('exam->>Instructions.ItemContent1AEIS')
            : t('exam->>Instructions.ItemContent1SPE')}
        </Typography>
        <ol type="a">
          <li>
            <Typography paragraph variant="body1">
              {isAeis
                ? t('exam->>Instructions.SubItemContent1AEIS')
                : t('exam->>Instructions.SubItemContent1SPE')}
            </Typography>
          </li>
          <li>
            <Typography paragraph variant="body1">
              {isAeis
                ? t('exam->>Instructions.SubItemContent2AEIS')
                : t('exam->>Instructions.SubItemContent2SPE')}
            </Typography>
          </li>
          {isAeis && (
            <>
              <li>
                <Typography paragraph variant="body1">
                  {t('exam->>Instructions.SubItemContent3AEIS')}
                </Typography>
              </li>
              <li>
                <Typography paragraph variant="body1">
                  {t('exam->>Instructions.SubItemContent6AEIS')}
                </Typography>
              </li>
              <li>
                <Typography paragraph variant="body1">
                  {t('exam->>Instructions.SubItemContent7AEIS')}
                </Typography>
              </li>
            </>
          )}
        </ol>
      </li>
      {isAeis ? (
        <>
          <br />
          <li>
            <Typography paragraph variant="body1">
              <b>{t('exam->>Instructions.ItemContent3AEIS')}</b>
            </Typography>
          </li>
          <Typography paragraph variant="body1" style={{ whiteSpace: 'nowrap' }}>
            {t('exam->>Instructions.ItemContent3iAEIS')}
          </Typography>
          <ol type="a">
            <li>
              <Typography paragraph variant="body1" style={{ whiteSpace: 'nowrap' }}>
                {t('exam->>Instructions.ItemContent3iiAEIS')}
              </Typography>
            </li>
            <li>
              <Typography paragraph variant="body1">
                {t('exam->>Instructions.ItemContent3iiiAEIS')}
              </Typography>
            </li>
            <li>
              <Typography paragraph variant="body1">
                {t('exam->>Instructions.ItemContent3ivAEIS')}
              </Typography>
            </li>
            <li>
              <Typography paragraph variant="body1">
                {t('exam->>Instructions.ItemContent3vAEIS')}
              </Typography>
            </li>
          </ol>
        </>
      ) : (
        <>
          <br />
          <li>
             <Typography paragraph variant="body1">
               <b>{t('exam->>Instructions.ItemContent2')}</b>
             </Typography>
          </li>
          <br />
          <li>
            <Typography paragraph variant="body1">
                <b>{t('exam->>Instructions.ItemContent4')}</b>
            </Typography>
          </li>
          <br />
          <li>
            <Typography paragraph variant="body1">
              {t('exam->>Instructions.ItemContent5')}
            </Typography>
          </li>
        </>
      )}
      {isAeis && (
        <>
          <br />
          <li>
            <Typography paragraph variant="body1" className={classes.boldContent}>
              <b>{t('exam->>Instructions.ItemContent2')}</b>
            </Typography>
          </li>
          <br />
          <li>
            <Typography variant="body1">
                <b>{t('exam->>Instructions.ItemContent3aAEIS')}</b>
                {t('exam->>Instructions.ItemContent3aiAEIS')}
                <span style={{ whiteSpace: 'nowrap' }}>
                    {t('exam->>Instructions.ItemContent3bAEIS').replace(/S-AEIS/g, 'S\u2011AEIS')}
                </span>
            </Typography>
            <Typography>
                {t('exam->>Instructions.ItemContent3biAEIS')}
            </Typography>
          </li>
          <br />
          <li>
            <Typography variant="body1">{t('exam->>Instructions.ItemContent5')}</Typography>
          </li>
        </>
      )}
    </ol>
  </>
);

interface FormContentProps {
  isSupplementary: boolean;
  examRegistration: NonExamRegistrationEligibilityResponse | undefined;
  uuid: string | undefined;
  isAEIS: boolean;
  modalState: boolean;
  setModalState: Dispatch<SetStateAction<boolean>>;
  examModeTitle: string;
  examType: string;
  handleStartRegistration: (value: IOptionType) => void;
  examTypesData: AdmissionExamLevelResponse | undefined;
  classes: Record<string,string>;
  t: TFunction;
  isDeclared: boolean;
  handleAgreement: () => void;
  acknowledgementAlert: boolean;
  waiveLateFee: boolean | undefined;
  createExamRegistrationMutation: CustomMutateReturn<
    AxiosResponse<CreateExamRegistrationResponse>,
    AxiosError<AxiosError<GenericErrorResponse>>,
    CreateNonExamRegistrationRequest,
    unknown
  >;
  setAcknowledgementAlert: Dispatch<SetStateAction<boolean>>;
  createErrorMessage?: string;
  setCreateErrorMessage: Dispatch<SetStateAction<string>>;
  path: string;
}

const FormContent = ({
  examRegistration,
  isSupplementary,
  uuid,
  isAEIS,
  modalState,
  setModalState,
  examModeTitle,
  examType,
  examTypesData,
  handleStartRegistration,
  classes,
  t,
  isDeclared,
  handleAgreement,
  acknowledgementAlert,
  createExamRegistrationMutation,
  waiveLateFee,
  setAcknowledgementAlert,
  createErrorMessage,
  setCreateErrorMessage,
  path,
}: FormContentProps) => {
  const history = useHistory();

  const levelSeekingAdmissionDefault: IOptionType | undefined = examRegistration?.draft
    ? examTypesData?.admissionLevelVacancies.find(
      (v) => v.code === examRegistration.draft?.admissionLevelCode
    )
    : undefined;

  const formHook = useForm<InstructionNonNationalFormData>({
    mode: 'onSubmit',
    resolver: joiResolver(instructionNonNationalSchema),
    defaultValues: examRegistration?.draft
      ? {
        levelSeekingAdmission: {
          ...levelSeekingAdmissionDefault,
        },
      }
      : undefined,
  });
  const { control, formState: {errors}, handleSubmit, watch, reset } = formHook;

  const { levelSeekingAdmission: levelSeekingAdmissionWatch } = watch();

  useEffect(() => {
    if (uuid) {
      const pathname = `/${exam}/${examRegistrations.route}/${nonNationalExaminations.route
        }/${uuid}/${path}/${isAEIS ? parentParticulars.route : candidateDetails.route}`;
      history.push({
        pathname,
        state: {
          examType,
          examTitle: examModeTitle,
          levelSeekingAdmission: levelSeekingAdmissionWatch,
          isSupplementary,
        },
      });
    }
  }, [
    examModeTitle,
    examType,
    history,
    isAEIS,
    isSupplementary,
    levelSeekingAdmissionWatch,
    path,
    uuid,
  ]);

  useEffect(() => {
    if (examRegistration?.draft) {
      reset({
        levelSeekingAdmission: { ...levelSeekingAdmissionDefault },
      });
    }
  }, [examRegistration, examRegistration?.draft, levelSeekingAdmissionDefault, reset]);

  useEffect(() => {
    setCreateErrorMessage('');
  }, [levelSeekingAdmissionWatch, setCreateErrorMessage]);

  const apiButtonClick = () => {
    const isDeclaredValidate = !waiveLateFee && !isDeclared && isSupplementary;
    if (isDeclaredValidate) {
      setAcknowledgementAlert(true);
    }
    handleSubmit((formData) => {
      if (!isDeclaredValidate) {
        if (examRegistration?.hasIexamRegistration) {
          setCreateErrorMessage(t('components->>IExamsExist.ContactSeab'));
          return;
        }

        if (
          examRegistration?.draft &&
          examRegistration.draft.admissionLevelCode !== formData.levelSeekingAdmission.code
        ) {
          setModalState(true);
        } else {
          handleStartRegistration(formData.levelSeekingAdmission);
        }
      }
    })();
  };

  return (
    <>
      <Box className={classes.getStartedContent}>
        <SectionHeader
          title={t('specialConsideration->>Instructions.GetStarted')}
          className={classes.getStartedTitle}
        />
        {levelSeekingAdmissionWatch &&
          examRegistration?.draft &&
          levelSeekingAdmissionWatch.code !== examRegistration?.draft?.admissionLevelCode && (
            <Typography paragraph variant="body1">
              {`${t('exam->>Instructions.GetStartedStart')} "${t(
                'exam->>NonNationalExam.LevelSeekingAdmission'
              )}" to `}
              <Typography className={classes.boldContent} display="inline">
                {levelSeekingAdmissionWatch.description}
                {', '}
              </Typography>
              {t('exam->>Instructions.GetStartedMid')}
              <Typography className={classes.boldContent} display="inline">
                {' '}
                {`${levelSeekingAdmissionDefault?.description || ''} `}
              </Typography>
              {t('exam->>Instructions.GetStartedEnd')}
              <b>{t('exam->>Instructions.GetStartedBoldStatement')}</b>
            </Typography>
          )}

        <ControlledAutocomplete
          className={classes.levelSeekingAdmission}
          id="inputSelect-levelSeekingAdmission"
          name="levelSeekingAdmission"
          control={control}
          label={
            <HtmlTrans i18nKey="exam->>NonNationalExam.YouAreNowSeekingAdmissionWithAdditionalInformation" />
          }
          placeholder={t('exam->>NonNationalExam.YouAreNowSeekingAdmissionPlaceholder')}
          options={examTypesData?.admissionLevelVacancies || []}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
          error={!!errors.levelSeekingAdmission || !!createErrorMessage}
          helperText={errors.levelSeekingAdmission?.message?.toString()}
          disableClearable
          readOnly
        />
        {isSupplementary && !waiveLateFee && (
          <Acknowledgement
            isAgreed={isDeclared}
            handleAgreement={handleAgreement}
            showError={acknowledgementAlert}
            content={t('exam->>Instructions.AcknowledgementConfirmContent')}
          />
        )}
        <Typography paragraph variant="caption" className={classes.vacancyNote}>
          {t('exam->>Instructions.NonNationalRegistrationVacancyTimeLimitReminder')}
        </Typography>
        {createErrorMessage && (
          <Typography variant="body2" className={classes.errString}>
            {createErrorMessage}
          </Typography>
        )}
        <ApiButton
          id="button-registration"
          className={classes.startApplicationButton}
          status={createExamRegistrationMutation.status}
          onClick={apiButtonClick}
        >
          {examRegistration?.draft
            ? t('exam->>Instructions.ContinueApplication')
            : t('exam->>RegisterExamInstructions.Start a new Registration')}
        </ApiButton>
      </Box>
      <ConfirmNewAcademicDialog
        modalState={modalState}
        onDialogClose={() => setModalState(false)}
        proceedClick={() => {
          setModalState(false);
          handleStartRegistration(levelSeekingAdmissionWatch);
        }}
        examLevel={examType.replace('_', '-')}
        previousAcademicLevel={levelSeekingAdmissionDefault?.description || ''}
      />
    </>
  );
};

const useCallApis = (
  examType: string,
  token: string | null,
  waiveLateFee: boolean,
  t: TFunction
) => {
  const [admissionLevelErrorMessage, setAdmissionLevelErrorMessage] = useState<string>('');
  const [eligibleErrorCode, setEligibleErrorCode] = useState<string | undefined>();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [createErrorMessage, setCreateErrorMessage] = useState<string>('');
  const [uuid, setUuid] = useState<string>();
  const [examTitle, setExamTitle] = useState<string>(t('exam->>RegisterExam.RegisterForAnExam'));
  const [isSupplementary, setIsSupplementary] = useState<boolean>(false);
  const [isDeclared, setIsDeclared] = useState<boolean>(false);
  const [acknowledgementAlert, setAcknowledgementAlert] = useState<boolean>(false);
  const [examTypeDisplay, setExamTypeDisplay] = useState<string>('-');
  const [isSuccessExamTypesData, setIsSuccessExamTypesData] = useState<boolean>(false);
  const [isSuccessExamRegistration, setIsSuccessExamRegistration] = useState<boolean>(false);

  const { data: examTypesData } = candidateService.exam.candidate.useGetAdmissionExamLevel(
    examType,
    {
      onSuccess: () => {
        setIsSuccessExamTypesData(true);
        setAdmissionLevelErrorMessage('');
      },
      onError: (err) => setAdmissionLevelErrorMessage(err?.response?.data.message as string),
    }
  );

  const {
    isError: eligibleError,
    data: examRegistration,
  } = candidateService.exam.candidate.useGetNonNationPendingExamRegistration(
    { examType: examType || '', token },
    {
      onSuccess: (res) => {
        setIsSuccessExamRegistration(true);
        if (res.currentExamRegistrationType === ExamRegistrationType.SUPPLEMENTARY) {
          setExamTitle(t('exam->>SupplementaryRegistration.RegisterForAnExamSupplementary'));
          setIsSupplementary(true);
          setIsDeclared(false);
        } else {
          setIsDeclared(true);
          setIsSupplementary(false);
        }
      },
      onError: (err) => {
        setEligibleErrorCode(err.response?.data.code || '');
        setErrorMessage(err.response?.data.message || '');
      },
      onSettled: () => {
        if (examType && examTypesData?.admissionLevelVacancies) {
          examTypesData?.admissionLevelVacancies.forEach((item) => {
            if (item.code === examType) {
              setExamTypeDisplay(item.description);
            }
          });
        }
      },
    }
  );

  const createExamRegistrationMutation = candidateService.exam.candidate.usePostCreateNonExamRegistration(
    (res) => {
      setUuid(res.data.uuid);
    },
    (err) => {
      if (
        err.response?.data.message ===
        'Invalid age limit for the selected Level Seeking Admission To.'
      ) {
        setCreateErrorMessage(t('exam->>Instructions.AgeRequirementError'));
      } else if (err.response?.data.code === '2010') {
        setCreateErrorMessage(t('exam->>Instructions.ApplicationPendingPayment'));
      } else {
        setCreateErrorMessage(err.response?.data.message || '');
      }
    }
  );

  const checkAcknowledgement = (callback: () => void) => {
    if (isSupplementary && !waiveLateFee) {
      if (!isDeclared) {
        setAcknowledgementAlert(true);
      } else {
        setAcknowledgementAlert(false);
        callback();
      }
    } else {
      callback();
    }
  };

 // wogaa tracking for candidate to start non national exam registration //
  const { transactionStart } = useWOGAA('myexamsseab-6457');

  const handleStartRegistration = (admissionLevelInput: IOptionType) => {
    checkAcknowledgement(() => {
      transactionStart();
      setCreateErrorMessage('');
      createExamRegistrationMutation.mutate({
        examType,
        token,
        admissionLevelCode: admissionLevelInput.code,
      });
    });
  };

  const handleAgreement = () => {
    setAcknowledgementAlert(false);
    setErrorMessage('');
    setIsDeclared(!isDeclared);
  };

  return {
    admissionLevelErrorMessage,
    allApiSuccess: isSuccessExamRegistration && isSuccessExamTypesData,
    eligibleError,
    eligibleErrorCode,
    examRegistration,
    examTypesData,
    examTypeDisplay,
    createExamRegistrationMutation,
    examTitle,
    isSupplementary,
    handleAgreement,
    handleStartRegistration,
    isDeclared,
    acknowledgementAlert,
    uuid,
    errorMessage,
    createErrorMessage,
    setCreateErrorMessage,
    setAcknowledgementAlert,
  };
};

export interface InstructionsContentsProps {
  instructionTitle: string;
  examType: string;
  examTitle: string;
  path: string;
}

const InstructionsContents = ({
  instructionTitle,
  examType,
  examTitle: examModeTitle,
  path,
}: InstructionsContentsProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const location = useLocation<{ waiveLateFee: boolean | undefined }>();

  const { token = null } = parseLocationSearch(location.search);
  const waiveLateFee = location.state?.waiveLateFee;
  const isAEIS = examType === EXAM_TYPE.AEIS || examType === EXAM_TYPE.SAEIS;

  const [modalState, setModalState] = useState<boolean>(false);

  const {
    admissionLevelErrorMessage,
    allApiSuccess,
    eligibleError,
    eligibleErrorCode,
    createExamRegistrationMutation,
    isSupplementary,
    handleAgreement,
    handleStartRegistration,
    isDeclared,
    acknowledgementAlert,
    examTypesData,
    uuid,
    examRegistration,
    examTitle,
    errorMessage,
    createErrorMessage,
    setAcknowledgementAlert,
    setCreateErrorMessage,
  } = useCallApis(examType, token, waiveLateFee || false, t);

  const titleHeader =
    t('exam->>RegisterExam.RegisterForAnExam') +
    (isSupplementary ? ` - ${t('exam->>Instructions.Supplementary')}` : '');

  if (eligibleError && eligibleErrorCode) {
    return (
      <NonExamNotAvailable
        examTitle={examTitle}
        examTypeDisplay={examModeTitle}
        errorMessage={errorMessage}
      />
    );
  }

  if (allApiSuccess) {
    return (
      <>
        <TitleHeader title={titleHeader} />
        <HeaderTemplate>
          <KeyValue
            keyText={t('exam->>RegisterExamInstructions.Exam Level')}
            valueText={examModeTitle}
          />
        </HeaderTemplate>
        <ContentTemplate>
          <SectionHeader title={instructionTitle} />
          <InstructionsContentsDetails t={t} classes={classes} isAeis={isAEIS} />
          <FormContent
            acknowledgementAlert={acknowledgementAlert}
            classes={classes}
            createExamRegistrationMutation={createExamRegistrationMutation}
            examModeTitle={examModeTitle}
            examRegistration={examRegistration}
            examType={examType}
            examTypesData={examTypesData}
            handleAgreement={handleAgreement}
            handleStartRegistration={handleStartRegistration}
            isAEIS={isAEIS}
            isDeclared={isDeclared}
            isSupplementary={isSupplementary}
            modalState={modalState}
            setModalState={setModalState}
            t={t}
            uuid={uuid}
            waiveLateFee={waiveLateFee}
            setAcknowledgementAlert={setAcknowledgementAlert}
            createErrorMessage={createErrorMessage}
            setCreateErrorMessage={setCreateErrorMessage}
            path={path}
          />
        </ContentTemplate>
      </>
    );
  }

  if (admissionLevelErrorMessage) {
    return <ContentTemplate>{admissionLevelErrorMessage}</ContentTemplate>;
  }

  // waiting api result
  return (
    <ContentTemplate transparent continuous>
      <Progress color="secondary" />
    </ContentTemplate>
  );
};

export default InstructionsContents;
